"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  UserPlus,
  Phone,
  Building2,
  CheckCircle2,
  AlertCircle,
  Mail,
  MapPin,
  Flame,
  Package,
  Layers,
  Sparkles,
  DollarSign,
  FileText,
  Sliders,
  UserCheck,
  Lock,
  MessageSquare,
} from "lucide-react";
import { Lead } from "@/lib/db";
import { WorkspaceConfig, getDefaultWorkspaceConfig, WORKSPACE_PRESETS } from "@/lib/workspacePresets";

interface TeamMemberOption {
  id: number;
  username: string;
  full_name: string;
  role: string;
  is_active?: number;
}

interface AddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLeadCreated: (lead: Lead, welcomeSent: boolean) => void;
  workspaceConfig?: WorkspaceConfig;
  currentUser?: { id: number; username: string; fullName?: string; companyName?: string; role?: string; phone?: string } | null;
  targetTenantId?: number;
  teamMembers?: TeamMemberOption[];
}

export default function AddLeadModal({
  isOpen,
  onClose,
  onLeadCreated,
  workspaceConfig,
  currentUser,
  targetTenantId,
  teamMembers,
}: AddLeadModalProps) {
  const isPlatformAdmin = currentUser?.id === 1 || currentUser?.role === "admin";
  const effectiveConfig =
    workspaceConfig ||
    getDefaultWorkspaceConfig(targetTenantId || currentUser?.id || 1, currentUser?.companyName);

  const profileKey = effectiveConfig.businessProfile || "trading";
  const preset = WORKSPACE_PRESETS[profileKey] || WORKSPACE_PRESETS.trading;

  const [teamList, setTeamList] = useState<TeamMemberOption[]>(teamMembers || []);

  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      setTeamList(teamMembers);
    } else if (isOpen) {
      const url = targetTenantId ? `/api/crm/team?tenant_id=${targetTenantId}` : "/api/crm/team";
      fetch(url)
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.team) {
            setTeamList(data.team);
          }
        })
        .catch(() => {});
    }
  }, [teamMembers, isOpen, targetTenantId]);

  const userRole = (currentUser?.role || (isPlatformAdmin ? "admin" : "agent")).toLowerCase();
  const isAdmin = userRole === "admin" || isPlatformAdmin;
  const isManager = userRole === "manager";
  const isAgent = !isAdmin && !isManager;

  const selectableMembers = useMemo(() => {
    return teamList.filter((m) => {
      if (m.is_active === 0) return false;
      const memberRole = (m.role || "agent").toLowerCase();
      if (isAdmin) return true;
      if (isManager) {
        return memberRole === "agent" || memberRole === "sales" || memberRole === "manager" || m.id === currentUser?.id;
      }
      return m.id === currentUser?.id || memberRole === "agent";
    });
  }, [teamList, isAdmin, isManager, currentUser?.id]);

  // Active stages from workspace configuration
  const availableStages =
    effectiveConfig.pipelineStages && effectiveConfig.pipelineStages.length > 0
      ? effectiveConfig.pipelineStages
      : [{ id: "Stage1", name: "Inquiry / RFQ" }];

  // Visible fields configured in Fields & Table Headers
  const visibleFields = effectiveConfig.fields.filter((f) => f.visible !== false);

  // Form states based on workspace fields
  const [formData, setFormData] = useState<Record<string, any>>({
    name: "",
    company_name: "",
    phone: "",
    email: "",
    service_type: "",
    quantity: "",
    unit: "",
    estimated_deal_value: "",
    currency: profileKey === "real_estate" ? "AED" : "INR",
    country_or_city: "",
    priority: "HOT",
    status: availableStages[0]?.name || "Inquiry / RFQ",
    notes: "",
  });

  const [customFieldsData, setCustomFieldsData] = useState<Record<string, string>>({});
  const [sendWelcomeWhatsapp, setSendWelcomeWhatsapp] = useState(true);
  const [createdLeadData, setCreatedLeadData] = useState<{
    lead: Lead;
    whatsappUrl: string;
    phone: string;
    whatsappDispatched: boolean;
  } | null>(null);
  const [customGreeting, setCustomGreeting] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);

  // Re-sync default stage and form fields whenever modal opens or configuration updates
  useEffect(() => {
    if (isOpen) {
      setErrorMsg(null);
      setSuccessBanner(null);
      setCreatedLeadData(null);
      setSendWelcomeWhatsapp(true);
      setCustomFieldsData({});
      const defaultStage = availableStages[0]?.name || "Inquiry / RFQ";
      const userPhone = currentUser?.phone || "";
      const company = (currentUser?.companyName || "").toLowerCase();
      let defaultCurr = "INR";
      if (userPhone.startsWith("+971") || company.includes("dubai") || company.includes("uae")) {
        defaultCurr = "AED";
      } else if (userPhone.startsWith("+1")) {
        defaultCurr = "USD";
      } else if (userPhone.startsWith("+44") || company.includes("london") || company.includes("uk")) {
        defaultCurr = "GBP";
      }

      setFormData({
        name: "",
        company_name: "",
        phone: "",
        email: "",
        service_type: "",
        quantity: "",
        unit: "",
        estimated_deal_value: "",
        currency: defaultCurr,
        country_or_city: "",
        priority: "HOT",
        status: defaultStage,
        assigned_rep: isAgent ? (currentUser?.fullName || currentUser?.username || "") : "",
        notes: "",
      });
    }
  }, [isOpen, effectiveConfig, profileKey, currentUser]);

  if (!isOpen) return null;

  const getField = (key: string) => effectiveConfig.fields.find((f) => f.key === key);
  const isFieldVisible = (key: string) => {
    const f = getField(key);
    // Commodity / manufacturing / freight volume fields MUST be explicitly present in workspaceConfig.fields
    if (["quantity", "unit"].includes(key)) {
      return Boolean(f && f.visible !== false);
    }
    return f ? f.visible !== false : true;
  };
  const getFieldLabel = (key: string, fallback: string) => {
    return getField(key)?.label || fallback;
  };
  const getFieldPlaceholder = (key: string, fallback: string) => {
    return getField(key)?.placeholder || fallback;
  };

  const customFields = effectiveConfig.fields.filter(
    (f) => f.key.startsWith("custom_") && f.visible !== false
  );

  const handleChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessBanner(null);

    // Validation based on required fields in workspaceConfig
    const nameField = getField("name");
    if (nameField?.required !== false && !formData.name?.trim()) {
      setErrorMsg(`${getFieldLabel("name", "Contact Person")} is required.`);
      return;
    }

    const phoneField = getField("phone");
    if (phoneField?.required !== false && !formData.phone?.trim()) {
      setErrorMsg(`${getFieldLabel("phone", "WhatsApp / Contact Phone")} is required.`);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: any = {
        name: formData.name.trim(),
        company_name: formData.company_name?.trim() || `${formData.name.trim()}'s Business`,
        phone: formData.phone.trim(),
        email: formData.email?.trim() || undefined,
        service_type: formData.service_type?.trim() || getFieldLabel("service_type", "Inquiry"),
        estimated_deal_value: Number(formData.estimated_deal_value) || 0,
        currency: formData.currency || "INR",
        country_or_city: formData.country_or_city?.trim() || "",
        priority: formData.priority || "HOT",
        status: formData.status || availableStages[0]?.name || "Inquiry / RFQ",
        assigned_rep: formData.assigned_rep?.trim() || undefined,
        notes: formData.notes?.trim() || undefined,
        quantity: formData.quantity ? Number(formData.quantity) : undefined,
        unit: formData.unit?.trim() || undefined,
        custom_fields: Object.keys(customFieldsData).length > 0 ? customFieldsData : undefined,
        send_welcome_whatsapp: sendWelcomeWhatsapp,
        custom_message: customGreeting?.trim() || undefined,
        tenant_id: targetTenantId || currentUser?.id,
      };

      const res = await fetch("/api/crm/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to add lead. Please verify inputs.");
      }

      if (data.whatsappDispatched) {
        setCreatedLeadData({
          lead: data.lead,
          whatsappUrl: data.whatsappUrl,
          phone: payload.phone,
          whatsappDispatched: true,
        });
        setSuccessBanner(`Lead added! WhatsApp welcome message dispatched to ${payload.phone}!`);
      } else if (sendWelcomeWhatsapp && data.whatsappUrl) {
        setCreatedLeadData({
          lead: data.lead,
          whatsappUrl: data.whatsappUrl,
          phone: payload.phone,
          whatsappDispatched: false,
        });
        setSuccessBanner("Lead created! Click below to send WhatsApp welcome message:");
      } else {
        setSuccessBanner(`Lead successfully added to "${payload.status}"!`);
        setTimeout(() => {
          onLeadCreated(data.lead, false);
          onClose();
        }, 700);
      }
    } catch (err: any) {
      console.error("[Add Lead Error]:", err);
      setErrorMsg(err.message || "Failed to add lead. Please check network connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col bg-slate-900 border border-slate-700/80 rounded-3xl shadow-2xl overflow-hidden text-slate-100">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between bg-slate-900/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center text-primary shadow-inner text-lg">
              {preset.icon || "📋"}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-lg text-white tracking-tight">
                  Add {preset.name ? `${preset.name} Lead` : "New Lead / Inquiry"}
                </h3>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300">
                  {currentUser?.companyName || "Workspace"}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Configured according to your workspace fields, table headers, and active pipeline stages.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {createdLeadData ? (
          <div className="p-8 flex flex-col items-center text-center space-y-6">
            <div className="w-16 h-16 rounded-3xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-xl">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <div className="space-y-1.5 max-w-md">
              <h3 className="text-xl font-bold text-white tracking-tight">Lead Successfully Added!</h3>
              <p className="text-xs text-slate-300">
                <span className="font-semibold text-white">{createdLeadData.lead.name}</span>
                {createdLeadData.lead.company_name ? ` (${createdLeadData.lead.company_name})` : ""} has been added to your CRM pipeline.
              </p>
            </div>

            {/* WhatsApp Status Box */}
            <div className={`w-full max-w-md p-4 rounded-2xl border text-left space-y-3.5 ${
              createdLeadData.whatsappDispatched
                ? "bg-emerald-950/40 border-emerald-500/40"
                : "bg-amber-950/30 border-amber-500/40"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className={`w-4 h-4 ${createdLeadData.whatsappDispatched ? "text-emerald-400" : "text-amber-400"}`} />
                  <span className="text-xs font-bold text-white">
                    {createdLeadData.whatsappDispatched ? "WhatsApp Message Dispatched" : "Instant 1-Click WhatsApp Delivery"}
                  </span>
                </div>
                <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                  createdLeadData.whatsappDispatched
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                    : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                }`}>
                  {createdLeadData.whatsappDispatched ? "✓ Meta Cloud API" : "Action Required"}
                </span>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                {createdLeadData.whatsappDispatched
                  ? `An outreach template was delivered to +${createdLeadData.phone} via Meta WhatsApp API.`
                  : `Meta Cloud API strictly restricts automated freeform messages to cold contacts outside the 24h customer window. Click below to launch WhatsApp Web or App directly with the pre-filled message:`}
              </p>

              {createdLeadData.whatsappUrl && (
                <a
                  href={createdLeadData.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/40 transition-all cursor-pointer"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>{createdLeadData.whatsappDispatched ? "Open WhatsApp Conversation ➔" : "Send Pre-Filled WhatsApp Message (1-Click) ➔"}</span>
                </a>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  onLeadCreated(createdLeadData.lead, createdLeadData.whatsappDispatched);
                  onClose();
                }}
                className="px-6 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white text-xs font-bold shadow-lg shadow-primary/20 transition-all cursor-pointer"
              >
                Close &amp; View in Pipeline
              </button>
            </div>
          </div>
        ) : (
        /* Form Body */
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {errorMsg && (
            <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs flex items-center gap-2.5">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successBanner && (
            <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{successBanner}</span>
            </div>
          )}

          {/* Section 1: Contact & Identity */}
          <div className="space-y-3">
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <span>👤 1. {getFieldLabel("name", "Contact Person")} &amp; Identity</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {/* Contact Name */}
              {isFieldVisible("name") && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    {getFieldLabel("name", "Contact Person")}
                    {getField("name")?.required !== false && <span className="text-rose-400 ml-1">*</span>}
                  </label>
                  <input
                    type="text"
                    required={getField("name")?.required !== false}
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder={getFieldPlaceholder("name", "e.g. Contact Name")}
                    className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              )}

              {/* Company / Organization */}
              {isFieldVisible("company_name") && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    {getFieldLabel("company_name", "Company / Organization")}
                    {getField("company_name")?.required && <span className="text-rose-400 ml-1">*</span>}
                  </label>
                  <div className="relative">
                    <Building2 className="w-3.5 h-3.5 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="text"
                      required={Boolean(getField("company_name")?.required)}
                      value={formData.company_name}
                      onChange={(e) => handleChange("company_name", e.target.value)}
                      placeholder={getFieldPlaceholder("company_name", "e.g. Enterprise / Firm Name")}
                      className="w-full pl-9 pr-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>
              )}

              {/* Phone */}
              {isFieldVisible("phone") && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    {getFieldLabel("phone", "WhatsApp / Contact Phone")}
                    {getField("phone")?.required !== false && <span className="text-rose-400 ml-1">*</span>}
                  </label>
                  <div className="relative">
                    <Phone className="w-3.5 h-3.5 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="tel"
                      required={getField("phone")?.required !== false}
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder={getFieldPlaceholder("phone", "+91 98765 43210")}
                      className="w-full pl-9 pr-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>
              )}

              {/* Email */}
              {isFieldVisible("email") && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    {getFieldLabel("email", "Official Email")}
                    {getField("email")?.required && <span className="text-rose-400 ml-1">*</span>}
                  </label>
                  <div className="relative">
                    <Mail className="w-3.5 h-3.5 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="email"
                      required={Boolean(getField("email")?.required)}
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder={getFieldPlaceholder("email", "client@example.com")}
                      className="w-full pl-9 pr-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Specification Scope */}
          <div className="space-y-3 pt-2 border-t border-slate-800/80">
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <span>📦 2. {getFieldLabel("service_type", "Product / Service Scope")} &amp; Terms</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {/* Product / Service Type */}
              {isFieldVisible("service_type") && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    {getFieldLabel("service_type", "Product / Service Description")}
                    {getField("service_type")?.required && <span className="text-rose-400 ml-1">*</span>}
                  </label>
                  <input
                    type="text"
                    required={Boolean(getField("service_type")?.required)}
                    value={formData.service_type}
                    onChange={(e) => handleChange("service_type", e.target.value)}
                    placeholder={getFieldPlaceholder("service_type", "e.g. Commodity, Scope, or Service details")}
                    className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              )}

              {/* Quantity */}
              {isFieldVisible("quantity") && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    {getFieldLabel("quantity", "Quantity / Volume")}
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.quantity}
                    onChange={(e) => handleChange("quantity", e.target.value)}
                    placeholder={getFieldPlaceholder("quantity", "500")}
                    className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              )}

              {/* Unit */}
              {isFieldVisible("unit") && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    {getFieldLabel("unit", "Unit of Measurement")}
                  </label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => handleChange("unit", e.target.value)}
                    placeholder={getFieldPlaceholder("unit", "e.g. MT, Units, Containers")}
                    className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              )}

              {/* Deal Value & Currency */}
              {isFieldVisible("estimated_deal_value") && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    {getFieldLabel("estimated_deal_value", "Estimated Value / Amount")}
                    {getField("estimated_deal_value")?.required && <span className="text-rose-400 ml-1">*</span>}
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={formData.currency}
                      onChange={(e) => handleChange("currency", e.target.value)}
                      className="w-24 px-2.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs font-bold text-white focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
                    >
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="AED">AED (د.إ)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                    </select>
                    <input
                      type="number"
                      step="any"
                      required={Boolean(getField("estimated_deal_value")?.required)}
                      value={formData.estimated_deal_value}
                      onChange={(e) => handleChange("estimated_deal_value", e.target.value)}
                      placeholder={getFieldPlaceholder("estimated_deal_value", "150000")}
                      className="flex-1 px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>
              )}

              {/* City / Port / Location */}
              {isFieldVisible("country_or_city") && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    {getFieldLabel("country_or_city", "Location / Destination / City")}
                  </label>
                  <div className="relative">
                    <MapPin className="w-3.5 h-3.5 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="text"
                      value={formData.country_or_city}
                      onChange={(e) => handleChange("country_or_city", e.target.value)}
                      placeholder={getFieldPlaceholder("country_or_city", "e.g. Nhava Sheva / Mumbai")}
                      className="w-full pl-9 pr-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Dynamic Custom Fields */}
          {customFields.length > 0 && (
            <div className="space-y-3 pt-2 border-t border-slate-800/80">
              <div className="flex items-center justify-between">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>3. Custom Workspace Fields</span>
                </h4>
                <span className="text-[10px] text-cyan-400/80 font-medium bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 rounded-full">
                  Fields &amp; Table headers
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {customFields.map((field) => (
                  <div key={field.key} className={field.type === "textarea" ? "md:col-span-2" : ""}>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      {field.label}
                      {field.required && <span className="text-rose-400 ml-1">*</span>}
                    </label>
                    {field.type === "textarea" ? (
                      <textarea
                        rows={2}
                        value={customFieldsData[field.key] || ""}
                        onChange={(e) =>
                          setCustomFieldsData((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        placeholder={field.placeholder || `Enter ${field.label}`}
                        className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                      />
                    ) : field.type === "select" && field.options && field.options.length > 0 ? (
                      <select
                        value={customFieldsData[field.key] || ""}
                        onChange={(e) =>
                          setCustomFieldsData((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs font-semibold text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                      >
                        <option value="">Select {field.label}...</option>
                        {field.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type === "number" ? "number" : "text"}
                        value={customFieldsData[field.key] || ""}
                        onChange={(e) =>
                          setCustomFieldsData((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        placeholder={field.placeholder || `Enter ${field.label}`}
                        className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 4: Pipeline Stage & Priority */}
          <div className="space-y-3 pt-2 border-t border-slate-800/80">
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <span>⚡ 4. Pipeline Stage &amp; Urgency</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {/* Pipeline Stage - Strictly matching active stages */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                  <Sliders className="w-3.5 h-3.5 text-blue-400" />
                  Initial Pipeline Stage <span className="text-rose-400">*</span>
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs font-bold text-white focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
                >
                  {availableStages.map((stage) => (
                    <option key={stage.id} value={stage.name}>
                      {stage.name}
                    </option>
                  ))}
                </select>
                <span className="text-[10px] text-slate-500 mt-1 block">
                  Mapped directly from active workspace stages
                </span>
              </div>

              {/* Assigned Rep Selector (RBAC Matrix) */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-emerald-400" />
                    Assigned Rep
                  </span>
                  {isAgent && (
                    <span className="text-[10px] text-amber-500 flex items-center gap-0.5">
                      <Lock className="w-2.5 h-2.5" /> Assigned to you
                    </span>
                  )}
                </label>
                <select
                  value={formData.assigned_rep || ""}
                  disabled={isAgent}
                  onChange={(e) => handleChange("assigned_rep", e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs font-bold text-white focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                >
                  <option value="">Unassigned</option>
                  {selectableMembers.map((m) => (
                    <option key={m.id} value={m.full_name}>
                      {m.full_name} ({m.role === "admin" ? "Super Admin" : m.role === "manager" ? "Sales Manager" : "Sales Agent"})
                    </option>
                  ))}
                </select>
                <span className="text-[10px] text-slate-500 mt-1 block">
                  Select team member to assign lead
                </span>
              </div>

              {/* Priority */}
              {isFieldVisible("priority") && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                    <Flame className="w-3.5 h-3.5 text-amber-400" />
                    {getFieldLabel("priority", "Priority")}
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => handleChange("priority", e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs font-bold text-white focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
                  >
                    <option value="HOT">🔥 HOT (High Priority Follow-up)</option>
                    <option value="WARM">🟡 WARM (Active Evaluation)</option>
                    <option value="COLD">❄️ COLD (General Inquiry)</option>
                  </select>
                </div>
              )}

              {/* Notes / Terms */}
              {isFieldVisible("notes") && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-slate-400" />
                    {getFieldLabel("notes", "Notes & Incoterms / Terms")}
                  </label>
                  <textarea
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    placeholder={getFieldPlaceholder("notes", "e.g. Payment: 100% LC at sight, Delivery CIF within 30 days")}
                    className="w-full px-3.5 py-2.5 bg-slate-800/90 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Section 4: Direct WhatsApp Notification */}
          <div className="pt-2 border-t border-slate-800/80">
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={sendWelcomeWhatsapp}
                  onChange={(e) => setSendWelcomeWhatsapp(e.target.checked)}
                  className="w-4 h-4 rounded text-emerald-500 focus:ring-emerald-500 border-slate-700 bg-slate-800 cursor-pointer"
                />
                <span className="text-xs font-bold text-slate-200 flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />
                  Send Instant WhatsApp Message to Client
                </span>
              </label>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold uppercase tracking-wider">
                Default: ON
              </span>
            </div>

            {sendWelcomeWhatsapp && (
              <div className="mt-2 p-3 rounded-xl bg-slate-800/60 border border-slate-700 text-xs text-slate-400 space-y-1.5">
                <span className="font-semibold text-slate-300">Outbound Acknowledgment Preview:</span>
                <p className="italic text-[11px] leading-relaxed">
                  "Hello {formData.name || "Client"}! Thank you for connecting with {formData.company_name || currentUser?.companyName || "our team"}. We have logged your inquiry regarding *{formData.service_type || "your requirements"}*. Our sales desk will share commercial terms shortly."
                </p>
              </div>
            )}
          </div>
          </div>

          {/* Docked Sticky Footer Actions */}
          <div className="p-4 px-6 border-t border-slate-800 bg-slate-900/95 backdrop-blur-md flex items-center justify-end gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-slate-700 hover:bg-slate-800 text-xs font-semibold text-slate-300 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white text-xs font-bold shadow-lg shadow-primary/20 flex items-center gap-2 disabled:opacity-50 transition-all cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Adding to Pipeline...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Save to Pipeline</span>
                </>
              )}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
